/* Presentation decisions for column profiles and drift.
 *
 * Two rules from the contract shape everything here, and both are properties the platform holds at
 * the substrate that a formatter can quietly destroy.
 *
 * **A null is "not measured" and never a zero.** `completeness` is null for an *empty relation*,
 * because an empty table is not fully complete and 1.0 would put a perfect score on a table with
 * nothing in it — while a column that is entirely null is the different case and reports a measured
 * 0.0. Those are two different facts the API deliberately distinguishes, and rendering either as `0%`
 * collapses them. Same for `dominant_share` (null when nothing was retained), `distinct_change` and
 * `values_were_replaced`.
 *
 * **`exact` travels with every figure and must be shown beside it.** The contract states why: the same
 * 3% movement is a finding when measured and noise when it sits inside the estimator's error bar, and
 * a reader cannot tell which without it. A theta sketch at k=1024 carries about 3% relative standard
 * error, so a screen that presented an estimate as a measurement would invite somebody to act on the
 * error bar.
 */

import type { ColumnDrift, ColumnSketch } from '@/shared/api/generated/catalog';
import { compactNumber } from '@/shared/model/number';

/** What a null renders as. Never `0`, never blank. */
export const NOT_MEASURED = '—';

/** The smallest share this rendering can state. Below it, a positive share is reported as a bound. */
export const SMALLEST_STATED_SHARE = 0.001;

/** A share as a percentage, or `NOT_MEASURED` when the platform did not measure one.
 *
 * **A positive share below the display precision reads as a bound, not as zero**, and that was found by
 * looking at the screen: `dominant_share` for a near-unique column came back as `0.000247` and rendered
 * `0.0%` — indistinguishable from the *measured* zero this module exists to keep distinct, and a
 * statement that no value appears where the truth is one row in four thousand.
 *
 * It is the same class as the defect §9 records at migration 0047, where a real per-row price of
 * `0.0000004` quantised to zero in a `numeric(20, 6)` column and every line item for that meter costed
 * nothing while the meter counted as priced. The fix there was to refuse a positive rate that rounds to
 * zero; the fix here is to say `<0.1%` rather than assert a zero the platform did not measure.
 */
export function share(value: number | null): string {
  if (value === null) return NOT_MEASURED;
  if (value > 0 && value < SMALLEST_STATED_SHARE) {
    return `<${(SMALLEST_STATED_SHARE * 100).toFixed(1)}%`;
  }
  return `${(value * 100).toFixed(1)}%`;
}

/** A count, or `NOT_MEASURED`. Separated from `share` so a zero count still reads as a zero. */
export function count(value: number | null): string {
  if (value === null) return NOT_MEASURED;
  return value.toLocaleString();
}

/** Which distinct figure a row actually has, and it must say which.
 *
 * The source's single grouping produces an exact distinct count for free, so a fresh scan has one —
 * and §9 records the defect of publishing `(k-1)/theta` with a ~3% bar instead, on exactly the
 * high-cardinality keys where the figure matters. **A merge drops it**, because two exact distinct
 * counts do not add: the same value may appear in both partitions. So the honest rendering names its
 * own provenance rather than showing one number and hoping.
 */
export interface DistinctReading {
  readonly value: string;
  readonly measured: boolean;
}

export function distinctReading(sketch: ColumnSketch): DistinctReading {
  if (sketch.distinct_exact !== null) {
    return { value: sketch.distinct_exact.toLocaleString(), measured: true };
  }
  /* Rounded, because the estimator's own error bar is ~3%: rendering `4,183.871` would put four
   * significant figures of false precision on a figure good to two. Unreachable in production today —
   * all three adapters set `distinct_exact` unconditionally and nothing merges — and kept because a
   * merged sketch is the design, so the branch must be right when it arrives rather than written
   * then. */
  return {
    value: `~${compactNumber(Math.round(sketch.distinct_estimate))}`,
    measured: false,
  };
}

/** Whether a row's dominance figure was counted or bounded — read from the item, not from `exact`.
 *
 * **The first version keyed this on the payload's `exact` and that was wrong in the common case, on a
 * statement about a customer's data.** Three things make it wrong, all checked in the adapters rather
 * than reasoned about. `exact` is one flag set from `distinct_values < SKETCH_K` and handed to *both*
 * sketches by all three adapters, so it is a fact about cardinality rather than about the summary.
 * The summary's truncation is `row_number() OVER (ORDER BY c DESC, d) <= k`, which drops the
 * *smallest* counts — so **rank 1 is always retained**, and `dominant_share` is
 * `items[0].count / rows_scanned` where that count is an exact per-group `count(*)`. And
 * `FrequentItem.error` becomes non-zero only in `merge`, which nothing in the platform calls.
 *
 * So a wide column's dominance share is a *measurement*, and marking it as an estimate — with a
 * tooltip saying the true share may be lower — was the inverse of this module's own rule, asserted
 * about a figure a steward acts on. The claim "the two sketches have independent bounds" was false
 * too: one flag, three adapters, one condition.
 *
 * `error` is the qualifier that actually answers the question, it is on the wire, and reading it
 * costs nothing in disclosure terms — it is a count, not a value.
 */
export function dominantReading(sketch: ColumnSketch): DistinctReading {
  const top = sketch.frequent_values[0];
  return {
    value: share(sketch.dominant_share),
    /* An absent share was not measured, so it is not a measurement however exact the counts are —
     * a caller should not have to add that. The first version returned `measured: true` here and
     * relied on the panel checking null separately, which is a property held by a consumer. */
    measured: sketch.dominant_share !== null && top?.error === 0,
  };
}

/** The share above which a column's rules pass for a reason nobody intended.
 *
 * Not a threshold the platform enforces — nothing here changes a verdict — but the number at which a
 * distribution is worth a person's attention. The contract calls `dominant_share` "the single most
 * useful number here: a column that is 98% one value is a column whose rules will pass for a reason
 * nobody intended", and 0.9 is where that starts being true rather than where it becomes extreme.
 */
export const DOMINANT_SHARE_NOTABLE = 0.9;

/** Fewer rows than this and the flag says nothing.
 *
 * In a table of one row every column holds exactly one value, so every column is "mostly one value"
 * and a warning beside each name is noise a reader learns to ignore — the reported case was a
 * one-row table with the sign on all fifteen columns. Twenty is where one value holding nine rows in
 * ten starts being a pattern rather than arithmetic. */
export const MIN_ROWS_FOR_DOMINANCE = 20;

export function isDominated(sketch: ColumnSketch): boolean {
  /* Deliberately not gated on any exactness flag, and the reason is stronger than the one first
   * written here: the summary retains the largest counts first, so rank 1 survives every truncation
   * and its count is exact. A k-bounded summary therefore costs this figure nothing. Even after a
   * merge — which nothing calls today — a Space-Saving count is an upper bound whose true value is
   * `count - error`, and the largest counts are still the retained ones, so a share over the
   * threshold remains evidence worth a person's attention.
   *
   * One honest limit, stated because a reader would otherwise be misled: the denominator is
   * `rows_scanned`, which **includes nulls**. A column that is 50% null and one single value among
   * the rest reports 0.50 and does not trip this threshold, so a dominated column can hide behind its
   * own incompleteness. That is the domain's definition rather than this threshold's, and the
   * `completeness` column beside it is what makes it visible. */
  return (
    sketch.rows_scanned >= MIN_ROWS_FOR_DOMINANCE &&
    sketch.dominant_share !== null &&
    sketch.dominant_share >= DOMINANT_SHARE_NOTABLE
  );
}

/** How a drift row should read. Four outcomes, because they are four different facts.
 *
 * `unmeasurable` is the one a careless screen loses: `values_were_replaced` is `boolean | null` and the
 * null exists because `jaccard` returns 0.0 for an empty union — so two all-null columns, or an empty
 * staging table profiled twice, would otherwise read as a **total population turnover on every
 * column**. §9 records that exact defect being fixed in the domain; this is the same rule one layer
 * out.
 */
export type DriftVerdict = 'replaced' | 'moved' | 'stable' | 'unmeasurable';

/** How much a *share* may move before it is worth reporting.
 *
 * One percentage point. Below that, two scans of an unchanged table routinely differ —
 * `rows_scanned` varies with concurrent writes — so a tighter bound would report noise as movement
 * on every column.
 */
export const SHARE_MOVEMENT_FLOOR = 0.01;

/** How much an *estimated* distinct count may move before it is worth reporting.
 *
 * **A separate constant, because `distinct_change` is not a share and applying the share floor to it
 * read estimator noise as a finding.** `compare` builds both sides from `estimate_overlap`, which is
 * `ThetaSketch.cardinality()` — a KMV estimate carrying ~3% relative standard error for any column
 * above k. Two such estimates give a difference whose standard deviation is around 4.4%, so a 1%
 * floor sits well inside a quarter of one sigma and a wide column reports `moved` on nothing at all.
 *
 * 15% is roughly three sigma on that arithmetic: a change this large is not the estimator. The cost
 * is stated rather than hidden — a genuine 5% turnover in a wide column's cardinality reads as
 * `stable` — and it is the right direction, because a drift screen that cries wolf on every wide
 * column is a screen nobody reads. `values_were_replaced` is unaffected and remains the sharper
 * signal, since Jaccard answers turnover directly rather than by differencing two cardinalities.
 */
export const ESTIMATED_DISTINCT_FLOOR = 0.15;

/** The floor that applies to this comparison, which depends on whether it was measured.
 *
 * `ColumnDrift.exact` is `before.is_exact && after.is_exact`, so it says exactly whether the two
 * cardinalities being differenced were enumerated or estimated. It was on the wire and read by
 * nothing, while this module's own docstring said `exact` "travels with every figure and must be
 * shown beside it" — the claim-without-mechanism shape, on the one figure being adjudicated.
 */
export function distinctFloorFor(drift: ColumnDrift): number {
  return drift.exact ? SHARE_MOVEMENT_FLOOR : ESTIMATED_DISTINCT_FLOOR;
}

export function driftVerdict(drift: ColumnDrift): DriftVerdict {
  if (drift.values_were_replaced === null) return 'unmeasurable';
  if (drift.values_were_replaced) return 'replaced';
  const moved =
    (drift.distinct_change !== null &&
      Math.abs(drift.distinct_change) >= distinctFloorFor(drift)) ||
    movedShare(drift.completeness_before, drift.completeness_after) ||
    movedShare(drift.dominant_share_before, drift.dominant_share_after);
  return moved ? 'moved' : 'stable';
}

function movedShare(before: number | null, after: number | null): boolean {
  /* A pair where either side is null is *not* movement — it is a comparison that cannot be drawn.
   * Treating null as 0 here would turn "we did not measure one side" into "it fell to nothing", which
   * is the absence-is-not-zero rule at its sharpest: the reader would chase a collapse that never
   * happened. */
  if (before === null || after === null) return false;
  return Math.abs(after - before) >= SHARE_MOVEMENT_FLOOR;
}

/** The tone for a verdict. A total map, so a fifth verdict is a compile error.
 *
 * The Profiles design's colours, and the only copy of them: a value set that turned over is amber, a
 * distribution that moved is blue, and "cannot say" is grey. The Profiles screen reads this map, so
 * the dataset page and the estate cannot colour one verdict two ways.
 *
 * §9 records `verdictTone`'s `default:` rendering an unknown verdict as the *permitted* tone in
 * `core.ai.evals`, which stripped a failing row of its badge. A `satisfies Record<…>` makes the
 * omission fail at build time in the file whose whole subject is that these outcomes stay distinct.
 */
export const DRIFT_TONE = {
  replaced: 'warn',
  moved: 'info',
  stable: 'ok',
  unmeasurable: 'unknown',
} as const satisfies Record<DriftVerdict, string>;

/** How many distinct values a column has relative to its rows, on five steps. 0 is not measured.
 *
 * Shared by the dataset page's glance card and the Profiles screen's column map, so "unique" means
 * one thing on both. */
export type CardinalityLevel = 0 | 1 | 2 | 3 | 4 | 5;

export function cardinalityLevel(sketch: ColumnSketch): CardinalityLevel {
  if (sketch.completeness === null || sketch.rows_scanned === 0) return 0;
  const distinct = sketch.distinct_exact ?? sketch.distinct_estimate;
  if (distinct <= 1) return 1;
  const ratio = distinct / sketch.rows_scanned;
  if (ratio >= 0.95) return 5;
  if (ratio >= 0.2) return 4;
  if (ratio >= 0.001) return 3;
  return 2;
}

/** The dataset's averages across its latest profile, for the glance card at the top of the tab.
 *
 * **Each average is over the columns it can be measured on**, and `null` when there are none: an
 * empty table's completeness is not 0%, it is unmeasured. Uniqueness is distinct values per row,
 * capped at 1 because an estimated distinct count can exceed the rows it was drawn from. "Stable" is
 * the share of *compared* columns that did not move — a column one profile could not measure is left
 * out rather than counted as changed. */
export interface ProfileAverages {
  readonly completeness: number | null;
  readonly uniqueness: number | null;
  readonly topShare: number | null;
  readonly stable: number | null;
  readonly compared: number;
  readonly changed: number;
}

function mean(values: readonly number[]): number | null {
  return values.length === 0
    ? null
    : values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function profileAverages(
  columns: readonly ColumnSketch[],
  drift: readonly ColumnDrift[] | null,
): ProfileAverages {
  const measured = columns.filter(
    (column) => column.completeness !== null && column.rows_scanned > 0,
  );
  const verdicts = (drift ?? [])
    .map(driftVerdict)
    .filter((verdict) => verdict !== 'unmeasurable');
  const changed = verdicts.filter((verdict) => verdict !== 'stable').length;
  return {
    completeness: mean(measured.map((column) => column.completeness ?? 0)),
    uniqueness: mean(
      measured.map((column) =>
        Math.min(
          1,
          (column.distinct_exact ?? column.distinct_estimate) / column.rows_scanned,
        ),
      ),
    ),
    topShare: mean(
      measured.flatMap((column) =>
        column.dominant_share === null ? [] : [column.dominant_share],
      ),
    ),
    stable: verdicts.length === 0 ? null : (verdicts.length - changed) / verdicts.length,
    compared: verdicts.length,
    changed,
  };
}
